import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { HttpClient } from '@/application/interfaces/http-client';
import { Repositories } from '@/infra/repositories';
import { type GetCartSummaryProps } from '@/infra/repositories/checkout/cart/get-cart-summary';
import { type Response as CartSummaryResponse } from '@/infra/services/checkout/cart/get-cart-summary';
import { cookies } from '@/utils/cookies';

import { QUERY_KEY } from './constants';

type QueryOptions<TData = CartSummaryResponse> = Omit<
  UseQueryOptions<CartSummaryResponse, Error, TData>,
  'queryKey' | 'queryFn'
>;

interface UseCardSummaryProps
  extends Pick<
    GetCartSummaryProps,
    'installments' | 'paymentMethod' | 'vouchers'
  > {}

export function useCartSummary<TData = CartSummaryResponse>(params?: {
  props?: Readonly<UseCardSummaryProps>;
  options?: QueryOptions<TData>;
}) {
  const cartId = cookies.client.get('cartId')?.value || '';

  const vouchers = params?.props?.vouchers ?? [];

  const query = useQuery<CartSummaryResponse, Error, TData>({
    queryKey: [
      QUERY_KEY,
      cartId,
      params?.props?.paymentMethod,
      params?.props?.installments,
      vouchers,
    ],
    queryFn: async () => {
      if (!cartId) {
        throw new Error('Cart ID not found');
      }

      try {
        return await Repositories.Checkout.Cart.getCartSummary({
          cartId,
          paymentMethod: params?.props?.paymentMethod,
          installments: params?.props?.installments,
          vouchers,
        });
      } catch (error) {
        if (error instanceof HttpClient.Response) {
          if (error.status === 404) {
            throw new Error('Cart not found');
          }
        }
        throw error;
      }
    },
    enabled: !!cartId,
    staleTime: 0,
    refetchOnMount: 'always',
    retry: 2,
    ...params?.options,
  });

  return { ...query, isLoading: query.isLoading, error: query.error };
}
